import {ShippingData} from "@/types/shippingData";
import axios from "axios";
import {Quantities} from "@/types/quantities";
import {
    XhosaResellerPricing,
    XhosaResellerPricingTier,
    XhosaApiResponse,
    XhosaTierRequest,
    XhosaPricingCache,
    XhosaTierCache
} from "@/types/xhosaPricing";

export interface DeliveryPeriod {
  start: string;
  end: string;
}

export interface DeliveryPeriodResponse {
  delivery_period: DeliveryPeriod;
}

export const getShippingData = async (quantities: Quantities, country: string): Promise<ShippingData> => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')

    let csrfTokenStr = ""

    if (csrfToken) {
        csrfTokenStr = csrfToken.getAttribute('content') ?? ""
    }

    if (csrfTokenStr !== "") {
        axios.defaults.headers.common = {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfTokenStr
        };
    }

    const calculation = await axios.post('/api/v2/shipping-costs', {
        quantities: quantities,
        country: country
    });

    return calculation.data;
}

export const getCartDeliveryPeriod = async (quantities: Quantities, country: string): Promise<DeliveryPeriodResponse> => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')

    let csrfTokenStr = ""

    if (csrfToken) {
        csrfTokenStr = csrfToken.getAttribute('content') ?? ""
    }

    if (csrfTokenStr !== "") {
        axios.defaults.headers.common = {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfTokenStr
        };
    }

    // Convert quantities to items array format
    const items = Object.entries(quantities)
        .filter(([sku, quantity]) => quantity > 0)
        .map(([sku, quantity]) => ({ product_sku: sku, quantity }));

    const response = await axios.post('/api/v2/delivery-estimates/cart', {
        country: country,
        items: items
    });

    return response.data;
}

// Xhosa Pricing API Base URL (through Laravel API proxy)
const XHOSA_API_BASE = '/api/v2/xhosa';

// Simple in-memory cache for pricing data
const pricingCache: XhosaPricingCache = {};
let tierCache: XhosaTierCache | null = null;

// Cache TTL in milliseconds (60 seconds for both pricing and tiers, aligned with backend)
const PRICING_CACHE_TTL = 60 * 1000;
const TIER_CACHE_TTL = 60 * 1000;

/**
 * Setup CSRF token and session authentication for API requests
 */
const setupAuthenticationForApi = () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    let csrfTokenStr = "";

    if (csrfToken) {
        csrfTokenStr = csrfToken.getAttribute('content') ?? "";
    }

    if (csrfTokenStr !== "") {
        // Configure axios for Laravel session authentication
        axios.defaults.headers.common = {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfTokenStr,
            'Accept': 'application/json',
        };
        
        // Ensure cookies are sent with requests (for session auth)
        axios.defaults.withCredentials = true;
    }

    return csrfTokenStr !== "";
};

/**
 * Get reseller pricing for a product from Xhosa API (IP-restricted)
 */
export const getXhosaResellerPricing = async (productId: string, useCache: boolean = true): Promise<XhosaResellerPricing[]> => {
    // Check cache first
    if (useCache && pricingCache[productId]) {
        const cached = pricingCache[productId];
        const now = Date.now();
        if (now - cached.timestamp < cached.ttl) {
            return cached.pricing;
        }
        // Cache expired, remove it
        delete pricingCache[productId];
    }

    // Setup authentication
    setupAuthenticationForApi();

    try {
        const response = await axios.get<XhosaApiResponse<XhosaResellerPricing[]>>(
            `${XHOSA_API_BASE}/reseller/products/${productId}?cache=${useCache}`
        );

        if (response.data.status === 'success' && response.data.data) {
            const pricing = response.data.data;
            
            // Cache the result
            if (useCache) {
                pricingCache[productId] = {
                    pricing,
                    timestamp: Date.now(),
                    ttl: PRICING_CACHE_TTL
                };
            }
            
            return pricing;
        } else {
            throw new Error(response.data.message || 'Failed to get reseller pricing');
        }
    } catch (error) {
        console.warn('Xhosa reseller pricing API failed:', error);
        throw error;
    }
};

/**
 * Determine reseller pricing tier based on revenue from Xhosa API (IP-restricted)
 */
export const getXhosaResellerTier = async (
    country: string, 
    currency: string, 
    revenue: number, 
    useCache: boolean = true
): Promise<XhosaResellerPricingTier> => {
    // Check cache first
    if (useCache && tierCache) {
        const now = Date.now();
        if (now - tierCache.timestamp < tierCache.ttl && tierCache.tier) {
            return tierCache.tier;
        }
        // Cache expired
        tierCache = null;
    }

    // Setup authentication
    setupAuthenticationForApi();

    try {
        const requestData: XhosaTierRequest = {
            country,
            currency,
            revenue
        };

        const response = await axios.post<XhosaApiResponse<any>>(
            `${XHOSA_API_BASE}/reseller/tier?cache=${useCache}`,
            requestData
        );

        if (response.data.status === 'success' && response.data.data) {
            // Transform API response to match interface - handle nested object format
            const rawTier = response.data.data;
            const tier: XhosaResellerPricingTier = {
                id: rawTier.id,
                name: rawTier.name,
                country: typeof rawTier.country === 'string' ? rawTier.country : rawTier.country.value,
                revenueThreshold: parseFloat(rawTier.revenueThreshold),
                currency: typeof rawTier.currency === 'string' ? rawTier.currency : rawTier.currency.value,
            };
            
            // Cache the result
            if (useCache) {
                tierCache = {
                    tier,
                    timestamp: Date.now(),
                    ttl: TIER_CACHE_TTL
                };
            }
            
            return tier;
        } else {
            throw new Error(response.data.message || 'Failed to get reseller tier');
        }
    } catch (error) {
        console.warn('Xhosa tier determination API failed:', error);
        throw error;
    }
};

/**
 * Get all available reseller tiers for a country from Xhosa API (IP-restricted)
 */
export const getXhosaAllResellerTiers = async (
    country: string = 'CH',
    useCache: boolean = true
): Promise<XhosaResellerPricingTier[]> => {
    // Setup authentication
    setupAuthenticationForApi();

    try {
        const response = await axios.get<XhosaApiResponse<any[]>>(
            `${XHOSA_API_BASE}/reseller/tiers/${country}?cache=${useCache}`
        );

        if (response.data.status === 'success' && response.data.data) {
            // Transform API response to match interface - handle nested object format
            const tiers: XhosaResellerPricingTier[] = response.data.data.map((tier: any) => ({
                id: tier.id,
                name: tier.name,
                country: typeof tier.country === 'string' ? tier.country : tier.country.value,
                revenueThreshold: parseFloat(tier.revenueThreshold),
                currency: typeof tier.currency === 'string' ? tier.currency : tier.currency.value,
            }));
            
            return tiers;
        } else {
            throw new Error(response.data.message || 'Failed to get all reseller tiers');
        }
    } catch (error) {
        console.warn('Xhosa all reseller tiers API failed:', error);
        throw error;
    }
};

/**
 * Check if Xhosa API is available by calling the health endpoint
 */
export const checkXhosaApiAvailability = async (): Promise<boolean> => {
    try {
        // Setup authentication
        setupAuthenticationForApi();

        const response = await axios.get<XhosaApiResponse<{available: boolean, message: string}>>(
            `${XHOSA_API_BASE}/health`
        );

        return response.data.status === 'success' && 
               response.data.data?.available === true;
    } catch (error) {
        console.warn('Xhosa API availability check failed:', error);
        return false;
    }
};

/**
 * Clear Xhosa pricing caches
 */
export const clearXhosaPricingCache = (): void => {
    Object.keys(pricingCache).forEach(key => delete pricingCache[key]);
    tierCache = null;
};

/**
 * API client for general application use
 */
export const api = {
    get: async (url: string) => {
        setupAuthenticationForApi();
        return await axios.get(url);
    },
    post: async (url: string, data?: any) => {
        setupAuthenticationForApi();
        return await axios.post(url, data);
    },
    put: async (url: string, data?: any) => {
        setupAuthenticationForApi();
        return await axios.put(url, data);
    },
    delete: async (url: string) => {
        setupAuthenticationForApi();
        return await axios.delete(url);
    }
};
